<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="closeDialog">
    <div class="modal-content permission-modal">
      <!-- Header -->
      <div class="modal-header">
        <h2 class="modal-title">
          {{
            $t("controlCenter.userManagement.permissionDialogTitle", {
              userName: userFullname,
            })
          }}
        </h2>
        <button class="close-button" @click="closeDialog">&times;</button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <div class="tabs">
          <div class="tab-btn active">
            {{ $t("controlCenter.userManagement.permissions") }}
          </div>
        </div>

        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <p>{{ $t("controlCenter.common.loading") }}</p>
        </div>
        <template v-else>
          <div class="tab-content-pane">
            <div
              v-for="group in permissionGroups"
              :key="group.name"
              class="permission-group"
            >
              <h3 class="section-title">{{ group.label }}</h3>
              <div class="permission-grid">
                <label
                  v-for="permission in group.permissions"
                  :key="permission.id"
                  class="checkbox-label"
                >
                  <input
                    type="checkbox"
                    :value="permission.id"
                    v-model="selectedPermissions"
                    class="checkbox-input"
                  />
                  <span>{{ permission.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button
          class="btn btn-secondary"
          @click="closeDialog"
          :disabled="saving"
        >
          {{ $t("controlCenter.userManagement.cancel") }}
        </button>
        <button class="btn btn-primary" @click="handleSave" :disabled="saving">
          <span v-if="saving">
            <i class="fas fa-spinner fa-spin"></i>
            {{ $t("controlCenter.userManagement.saving") }}
          </span>
          <span v-else>
            <i class="fas fa-save"></i>
            {{ $t("controlCenter.userManagement.saveChanges") }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { userApi } from "@/api/user";

export default {
  name: "UserPermissionDialog",
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      selectedPermissions: [],
      permissionGroups: [],
      initialPermissions: [],
      loading: false,
      saving: false,
    };
  },
  watch: {
    isOpen(val) {
      if (val && this.user) {
        this.fetchUserPermissions();
      }
    },
  },
  computed: {
    userFullname() {
      if (!this.user) return "";
      return `${this.user.firstName} ${this.user.lastName}`;
    },
  },
  methods: {
    async fetchUserPermissions() {
      if (!this.user) return;
      this.loading = true;
      try {
        const userId = this.getUserId(this.user.id);

        // 并行加载所有权限定义和用户当前权限
        const [allPerms, userData] = await Promise.all([
          userApi.getPermissions(),
          userApi.getUser(userId),
        ]);

        // 1. 处理所有权限并分组
        // 兼容处理 { content: [], total: 0 } 或 直接数组格式
        const permsData =
          allPerms.content ||
          allPerms.data?.content ||
          allPerms.data ||
          allPerms;
        this.processPermissions(Array.isArray(permsData) ? permsData : []);

        // 2. 设置用户当前选中的权限
        // 注意：userData 可能也是类似结构，或者直接是资源对象
        const userPermsArray =
          userData.permissions || userData.data?.permissions || [];
        this.initialPermissions = userPermsArray.map((p) =>
          typeof p === "object" ? p.id : p
        );

        this.selectedPermissions = [...this.initialPermissions];
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
        this.$message.error(this.$t("controlCenter.userManagement.permissionLoadError"));
      } finally {
        this.loading = false;
      }
    },
    processPermissions(perms) {
      const groups = {};
      const labelMap = {
        USER: this.$t("controlCenter.userManagement.permissionGroups.user"),
        PERMISSION: this.$t("controlCenter.userManagement.permissionGroups.permission"),
        GROUP: this.$t("controlCenter.userManagement.permissionGroups.group"),
        ACCOUNT: this.$t("controlCenter.userManagement.permissionGroups.account"),
        AUDIT: this.$t("controlCenter.userManagement.permissionGroups.audit"),
        REPORT: this.$t("controlCenter.userManagement.permissionGroups.report"),
      };

      perms.forEach((p) => {
        const name = p.name || "";
        const parts = name.split("_");

        // 提取关键词进行更有意义的分组
        let prefix = parts[0];

        // 针对特定组合优化分组
        if (name.includes("USER_GROUP")) prefix = "GROUP";
        else if (name.includes("PERMISSION")) prefix = "PERMISSION";
        else if (name.includes("USER")) prefix = "USER";

        const groupName = labelMap[prefix] || prefix;

        if (!groups[groupName]) {
          groups[groupName] = {
            name: prefix,
            label: groupName,
            permissions: [],
          };
        }

        groups[groupName].permissions.push({
          id: p.id,
          label: p.description || p.name,
          name: p.name,
        });
      });

      this.permissionGroups = Object.values(groups);
    },
    getUserId(id) {
      if (typeof id === "object" && id !== null) {
        if (id.value) {
          return `${id.value.mostSigBits}-${id.value.leastSigBits}`;
        }
        return `${id.mostSigBits}-${id.leastSigBits}`;
      }
      return id;
    },
    async handleSave() {
      if (!this.user) return;

      this.saving = true;
      try {
        const userId = this.getUserId(this.user.id);

        // 计算权限变更
        const toAddPerms = this.selectedPermissions.filter(
          (p) => !this.initialPermissions.includes(p)
        );
        const toRemovePerms = this.initialPermissions.filter(
          (p) => !this.selectedPermissions.includes(p)
        );

        // 执行 API 调用
        const promises = [];

        // 添加新权限
        toAddPerms.forEach((p) => {
          promises.push(userApi.assignPermission(userId, p));
        });

        // 移除旧权限
        toRemovePerms.forEach((p) => {
          promises.push(userApi.removePermission(userId, p));
        });

        if (promises.length > 0) {
          await Promise.all(promises);
        }

        this.$message.success(
          this.$t("controlCenter.userManagement.assignSuccess")
        );
        this.$emit("permission-updated");
        this.closeDialog();
      } catch (error) {
        console.error("Failed to save permissions:", error);
        this.$message.error(this.$t("controlCenter.userManagement.saveError"));
      } finally {
        this.saving = false;
      }
    },
    closeDialog() {
      this.selectedPermissions = [];
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px 0;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 850px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: modal-fade-in 0.3s ease;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px 25px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
}

.close-button {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #7f8c8d;
  transition: color 0.2s;
}

.modal-body {
  padding: 20px 25px;
  overflow-y: auto;
  flex: 1;
  position: relative;
  min-height: 200px;
}

.loading-state {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #7f8c8d;
}

.loading-state i {
  font-size: 32px;
  margin-bottom: 10px;
  color: #3498db;
}

.tabs {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 20px;
}

.tab-btn {
  padding: 10px 20px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #7f8c8d;
  border-bottom: 3px solid transparent;
  transition: all 0.3s;
}

.tab-btn.active {
  color: #3498db;
  border-bottom-color: #3498db;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #2c3e50;
  margin: 20px 0 10px 0;
  padding-bottom: 5px;
  border-bottom: 1px solid #f0f0f0;
}

.permission-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #34495e;
  cursor: pointer;
  padding: 5px 0;
}

.checkbox-input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.permission-sets-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.permission-set-item {
  display: block;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.permission-set-item:hover {
  background: #f0f2f5;
}

.set-main {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
}

.set-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.set-description {
  margin: 0 0 0 26px;
  font-size: 12px;
  color: #7f8c8d;
}

.modal-footer {
  padding: 15px 25px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn {
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background-color: #3498db;
  color: white;
}

.btn-secondary {
  background-color: #ecf0f1;
  color: #2c3e50;
}

@keyframes modal-fade-in {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .modal-overlay {
    align-items: flex-start;
    padding: 70px 10px 14px;
  }

  .modal-content {
    width: 100%;
    max-width: none;
    max-height: calc(100dvh - 84px);
    border-radius: 10px;
  }

  .modal-header {
    padding: 16px;
    align-items: flex-start;
    gap: 12px;
  }

  .modal-title {
    font-size: 17px;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .modal-body {
    padding: 14px 16px;
  }

  .tabs {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .tab-btn {
    flex: 1 0 auto;
    white-space: nowrap;
    padding: 10px 12px;
  }

  .permission-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .checkbox-label {
    align-items: flex-start;
    line-height: 1.45;
  }

  .set-description {
    margin-left: 0;
    margin-top: 6px;
    line-height: 1.5;
  }

  .modal-footer {
    flex-direction: column-reverse;
    padding: 14px 16px;
  }

  .modal-footer .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
